/**
 * WsClient — Socket.IO-compatible client over native WebSocket.
 *
 * Mimics the subset of the socket.io-client API that this codebase relies on
 * (on/off/emit/onAny/onAnyOutgoing/timeout/disconnect/connect/id) so existing
 * consumer code keeps working with minimal changes.
 *
 * Wire protocol: see WsProtocol.ts (`{event, data?, ackId?}` JSON envelopes).
 *
 * Reconnection: exponential backoff 1s → 2s → 4s → 8s → max 30s.
 * Heartbeat: client sends `__ping`, expects `__pong` within 10s.
 *
 * Background tabs: browsers throttle `setInterval`/`setTimeout` heavily once
 * a tab is hidden, which can delay both the heartbeat and the reconnect
 * backoff by minutes. To recover quickly once the user comes back, this
 * client also reacts to the Page Visibility API and the `online` event by
 * immediately reconnecting (or probing a possibly-"zombie" open socket)
 * instead of waiting for the next throttled timer tick.
 */

import { WS_INTERNAL_EVENTS, decodeWsMessage, encodeWsMessage } from "./WsProtocol.js";

// Generic event maps mirror the Socket.IO style: a record of event-name → handler signature.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEventsMap = Record<string, (...args: any[]) => any>;

type ReservedHandler =
  | "connect"
  | "disconnect"
  | "connect_error"
  | "reconnect"
  | "reconnect_attempt"
  | "reconnect_error"
  | "reconnect_failed";

/**
 * How long to wait for a `__pong` after an immediate foreground/online probe
 * before treating the socket as a dead "zombie" connection. Deliberately
 * shorter than the regular heartbeat watchdog (`heartbeatInterval * 2`) since
 * the user is actively looking at the page at this point.
 */
const ZOMBIE_PROBE_TIMEOUT_MS = 5000;

export interface WsClientOptions {
  /** Endpoint path, e.g. "/api/ws". */
  path: string;
  /** Connect immediately. Default true. */
  autoConnect?: boolean;
  /** Extra query parameters (becomes ?k=v on the WS URL). */
  query?: Record<string, string>;
  /**
   * Headers cannot be set on browser WebSockets — these values are
   * appended to the URL as query parameters instead.
   * Kept under the name `extraHeaders` for Socket.IO API compatibility.
   */
  extraHeaders?: Record<string, string>;
  /** Initial reconnect delay in ms. Default 1000. */
  reconnectionDelay?: number;
  /** Maximum reconnect delay in ms. Default 30000. */
  reconnectionDelayMax?: number;
  /** Heartbeat interval in ms. Default 10000. 0 disables. */
  heartbeatInterval?: number;
  /**
   * Called before each reconnect attempt (timer-, visibility- or
   * online-triggered — NOT the initial `connect()`) to refresh auth-related
   * query params, e.g. a token that may have expired since the client was
   * constructed. The returned record is merged into the existing query
   * params before rebuilding the connection URL.
   */
  refreshAuthParams?: () => Record<string, string> | Promise<Record<string, string>>;
}

interface PendingAck {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout> | null;
}

export class WsClient<
  ListenEvents extends AnyEventsMap = AnyEventsMap,
  EmitEvents extends AnyEventsMap = AnyEventsMap,
> {
  public id: string = "";
  public connected: boolean = false;
  public disconnected: boolean = true;

  private ws: WebSocket | null = null;
  private readonly path: string;
  private query: Record<string, string>;
  private readonly reconnectionDelay: number;
  private readonly reconnectionDelayMax: number;
  private readonly heartbeatInterval: number;
  private readonly refreshAuthParams?: () => Record<string, string> | Promise<Record<string, string>>;

  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();
  private anyListeners = new Set<(event: string, ...args: unknown[]) => void>();
  private anyOutgoingListeners = new Set<(event: string, ...args: unknown[]) => void>();
  private pendingAcks = new Map<string, PendingAck>();
  private ackSeq = 0;
  private nextAckTimeoutMs: number | null = null;

  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatWatchdog: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = true;
  private opened = false;

  constructor(opts: WsClientOptions) {
    this.path = opts.path;
    this.query = { ...(opts.query ?? {}), ...(opts.extraHeaders ?? {}) };
    this.reconnectionDelay = opts.reconnectionDelay ?? 1000;
    this.reconnectionDelayMax = opts.reconnectionDelayMax ?? 30000;
    this.heartbeatInterval = opts.heartbeatInterval ?? 10000;
    this.refreshAuthParams = opts.refreshAuthParams;
    if (opts.autoConnect !== false) {
      this.connect();
    }
  }

  public connect(): this {
    if (this.ws != null && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return this;
    }
    this.shouldReconnect = true;
    // Idempotent: addEventListener with the same bound function reference is
    // a no-op if already registered, so it's safe to call this on every
    // connect() (covers both the initial autoConnect and a manual reconnect
    // after a full disconnect(), which removes the listeners below).
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleOnline);
    }
    this.openSocket();
    return this;
  }

  public disconnect(): this {
    this.shouldReconnect = false;
    this.clearTimers();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.handleOnline);
    }
    if (this.ws != null) {
      try { this.ws.close(1000, "client disconnect"); } catch { /* noop */ }
      this.ws = null;
    }
    if (this.connected) {
      this.connected = false;
      this.disconnected = true;
      this.dispatchReserved("disconnect", "io client disconnect");
    }
    return this;
  }

  /** Alias for disconnect() to match socket.io-client. */
  public close(): this {
    return this.disconnect();
  }

  /** Register a one-shot timeout for the next emit's ack. */
  public timeout(ms: number): this {
    this.nextAckTimeoutMs = ms;
    return this;
  }

  public emit<E extends keyof EmitEvents & string>(
    event: E,
    ...args: Parameters<EmitEvents[E]>
  ): this {
    // Detect trailing ack callback (Socket.IO style: last arg is a function).
    const argsArr = args as unknown[];
    let ackCb: ((...a: unknown[]) => void) | null = null;
    if (argsArr.length > 0 && typeof argsArr[argsArr.length - 1] === "function") {
      ackCb = argsArr.pop() as (...a: unknown[]) => void;
    }
    const data = argsArr.length <= 1 ? argsArr[0] : argsArr;

    const envelope: { event: string; data?: unknown; ackId?: string } = { event, data };

    if (ackCb != null) {
      const ackId = `c${++this.ackSeq}`;
      envelope.ackId = ackId;
      const timeoutMs = this.nextAckTimeoutMs;
      this.nextAckTimeoutMs = null;
      // Match socket.io-client semantics:
      //   - Without .timeout(): callback signature is (data) => …, and the
      //     callback is never invoked with an error (if the ack never arrives,
      //     it's silently dropped).
      //   - With .timeout(ms): callback signature is (err, data) => …, where
      //     `err` is non-null only on timeout / disconnect.
      const hasTimeout = timeoutMs != null;
      const pending: PendingAck = {
        resolve: hasTimeout
          ? (d: unknown) => ackCb!(null, d)
          : (d: unknown) => ackCb!(d),
        reject: hasTimeout
          ? (err: Error) => ackCb!(err)
          : (_err: Error) => { /* swallow — untimed acks never reject in socket.io */ },
        timer: hasTimeout
          ? setTimeout(() => {
            const p = this.pendingAcks.get(ackId);
            if (p != null) {
              this.pendingAcks.delete(ackId);
              p.reject(new Error("operation has timed out"));
            }
          }, timeoutMs!)
          : null,
      };
      this.pendingAcks.set(ackId, pending);
    } else {
      this.nextAckTimeoutMs = null;
    }

    this.dispatchAnyOutgoing(event, ...(Array.isArray(data) ? (data as unknown[]) : [data]));

    if (this.ws != null && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(encodeWsMessage(envelope));
      } catch (e) {
        console.error("[WsClient] send failed", e);
      }
    }
    // Note: if not connected, the emit is silently dropped (matches Socket.IO default).
    return this;
  }

  public on<E extends keyof ListenEvents & string>(event: E, handler: ListenEvents[E]): this;
  public on(event: ReservedHandler, handler: (...args: unknown[]) => void): this;
  public on(event: string, handler: (...args: unknown[]) => void): this {
    let set = this.listeners.get(event);
    if (set == null) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return this;
  }

  public off<E extends keyof ListenEvents & string>(event: E, handler?: ListenEvents[E]): this;
  public off(event: ReservedHandler, handler?: (...args: unknown[]) => void): this;
  public off(event: string, handler?: (...args: unknown[]) => void): this {
    const set = this.listeners.get(event);
    if (set == null) return this;
    if (handler == null) {
      set.clear();
    } else {
      set.delete(handler);
    }
    return this;
  }

  public once<E extends keyof ListenEvents & string>(event: E, handler: ListenEvents[E]): this;
  public once(event: string, handler: (...args: unknown[]) => void): this {
    const wrapper = (...args: unknown[]) => {
      (this as WsClient).off(event, wrapper);
      handler(...args);
    };
    return (this as WsClient).on(event, wrapper) as this;
  }

  public onAny(fn: (event: string, ...args: unknown[]) => void): this {
    this.anyListeners.add(fn);
    return this;
  }

  public offAny(fn?: (event: string, ...args: unknown[]) => void): this {
    if (fn == null) this.anyListeners.clear();
    else this.anyListeners.delete(fn);
    return this;
  }

  public onAnyOutgoing(fn: (event: string, ...args: unknown[]) => void): this {
    this.anyOutgoingListeners.add(fn);
    return this;
  }

  public offAnyOutgoing(fn?: (event: string, ...args: unknown[]) => void): this {
    if (fn == null) this.anyOutgoingListeners.clear();
    else this.anyOutgoingListeners.delete(fn);
    return this;
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  private buildUrl(): string {
    const proto = (typeof location !== "undefined" && location.protocol === "https:") ? "wss:" : "ws:";
    const host = typeof location !== "undefined" ? location.host : "localhost";
    const params = new URLSearchParams(this.query);
    const qs = params.toString();
    return `${proto}//${host}${this.path}${qs ? "?" + qs : ""}`;
  }

  private openSocket(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const url = this.buildUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.error("[WsClient] failed to open", e);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.opened = false;

    ws.onopen = () => {
      this.opened = true;
      this.connected = true;
      this.disconnected = false;
      this.reconnectAttempt = 0;
      this.id = `ws_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      this.startHeartbeat();
      this.dispatchReserved("connect");
    };

    ws.onmessage = (ev) => {
      const raw = typeof ev.data === "string" ? ev.data : "";
      if (raw === "") return;
      const msg = decodeWsMessage(raw);
      if (msg == null) return;
      this.handleIncoming(msg);
    };

    ws.onerror = () => {
      // Browser WS API doesn't surface details; just record.
      if (!this.opened) {
        this.dispatchReserved("connect_error", new Error("connect_error"));
      }
    };

    ws.onclose = (ev) => {
      this.clearHeartbeat();
      const wasConnected = this.connected;
      this.connected = false;
      this.disconnected = true;
      this.ws = null;
      // Reject any in-flight acks.
      for (const [id, pending] of this.pendingAcks) {
        if (pending.timer != null) clearTimeout(pending.timer);
        pending.reject(new Error("disconnected"));
        this.pendingAcks.delete(id);
      }
      if (wasConnected) {
        this.dispatchReserved("disconnect", ev.reason || "transport close");
      }
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    };
  }

  /**
   * Re-runs `refreshAuthParams` (if configured) and merges the result into
   * `this.query` before opening a new socket. Used for reconnects (timer-,
   * visibility- or online-triggered) — NOT for the initial `connect()`,
   * whose query params are already fresh at construction time.
   */
  private async openSocketWithFreshAuth(): Promise<void> {
    if (this.refreshAuthParams != null) {
      try {
        const fresh = await this.refreshAuthParams();
        this.query = { ...this.query, ...fresh };
      } catch (e) {
        console.error("[WsClient] refreshAuthParams failed", e);
      }
    }
    this.openSocket();
  }

  private readonly handleVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.checkConnectionNow();
    }
  };

  private readonly handleOnline = (): void => {
    this.checkConnectionNow();
  };

  /**
   * Immediately check/repair the connection instead of waiting for a
   * throttled timer. Triggered when the tab is foregrounded or the browser
   * reports it's back online.
   */
  private checkConnectionNow(): void {
    if (!this.shouldReconnect) return;
    const state = this.ws?.readyState;
    if (state === undefined || state === WebSocket.CLOSED || state === WebSocket.CLOSING) {
      // Not connected (or no socket at all) — skip any pending backoff delay
      // and reconnect right away.
      if (this.reconnectTimer != null) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      void this.openSocketWithFreshAuth();
      return;
    }
    if (state === WebSocket.OPEN) {
      // The socket looks open, but after a long background/suspend period it
      // may be a "zombie" (dead on the wire, undetected yet). Probe right away
      // with a short watchdog instead of waiting for the next regular
      // heartbeat tick, which may be minutes away if it was throttled.
      this.sendRaw({ event: WS_INTERNAL_EVENTS.PING });
      if (this.heartbeatWatchdog != null) clearTimeout(this.heartbeatWatchdog);
      this.heartbeatWatchdog = setTimeout(() => {
        console.warn("[WsClient] zombie connection detected on foreground/online — closing socket");
        if (this.ws != null) {
          try { this.ws.close(); } catch { /* noop */ }
        }
      }, ZOMBIE_PROBE_TIMEOUT_MS);
    }
    // CONNECTING: an attempt is already in flight — let it finish naturally.
  }

  private handleIncoming(msg: { event: string; data?: unknown; ackId?: string; error?: string }): void {
    // Internal protocol handling.
    if (msg.event === WS_INTERNAL_EVENTS.PONG) {
      this.resetHeartbeatWatchdog();
      return;
    }
    if (msg.event === WS_INTERNAL_EVENTS.PING) {
      // Server-initiated heartbeat ping — respond with pong.
      this.sendRaw({ event: WS_INTERNAL_EVENTS.PONG });
      return;
    }
    if (msg.event === WS_INTERNAL_EVENTS.ACK) {
      // Ack response for a previous emit.
      const ackId = msg.ackId;
      if (ackId == null) return;
      const pending = this.pendingAcks.get(ackId);
      if (pending == null) return;
      this.pendingAcks.delete(ackId);
      if (pending.timer != null) clearTimeout(pending.timer);
      if (msg.error != null) pending.reject(new Error(msg.error));
      else pending.resolve(msg.data);
      return;
    }

    // Regular event delivery. If the server included an ackId, provide
    // a callback as the trailing handler argument so Socket.IO-style
    // listeners can `cb(result)` to acknowledge.
    const data = msg.data;
    const args: unknown[] = Array.isArray(data) ? [...(data as unknown[])] : (data !== undefined ? [data] : []);
    if (msg.ackId != null) {
      const ackId = msg.ackId;
      let sent = false;
      const ackCb = (...result: unknown[]) => {
        if (sent) return;
        sent = true;
        const payload = result.length <= 1 ? result[0] : result;
        this.sendRaw({ event: WS_INTERNAL_EVENTS.ACK, ackId, data: payload });
      };
      args.push(ackCb);
    }

    this.dispatchAny(msg.event, ...args);
    const set = this.listeners.get(msg.event);
    if (set == null) return;
    for (const h of set) {
      try { h(...args); }
      catch (e) { console.error(`[WsClient] handler for ${msg.event} threw`, e); }
    }
  }

  private dispatchAny(event: string, ...args: unknown[]): void {
    for (const fn of this.anyListeners) {
      try { fn(event, ...args); }
      catch (e) { console.error("[WsClient] onAny handler threw", e); }
    }
  }

  private dispatchAnyOutgoing(event: string, ...args: unknown[]): void {
    for (const fn of this.anyOutgoingListeners) {
      try { fn(event, ...args); }
      catch (e) { console.error("[WsClient] onAnyOutgoing handler threw", e); }
    }
  }

  private dispatchReserved(event: ReservedHandler, ...args: unknown[]): void {
    const set = this.listeners.get(event);
    if (set == null) return;
    for (const h of set) {
      try { h(...args); }
      catch (e) { console.error(`[WsClient] reserved handler ${event} threw`, e); }
    }
  }

  private sendRaw(env: { event: string; data?: unknown; ackId?: string }): void {
    if (this.ws == null || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(encodeWsMessage(env)); } catch { /* noop */ }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    if (this.heartbeatInterval <= 0) return;
    this.heartbeatTimer = setInterval(() => {
      this.sendRaw({ event: WS_INTERNAL_EVENTS.PING });
      this.resetHeartbeatWatchdog();
    }, this.heartbeatInterval);
  }

  private resetHeartbeatWatchdog(): void {
    if (this.heartbeatWatchdog != null) clearTimeout(this.heartbeatWatchdog);
    // Allow 2× interval before declaring the connection dead.
    this.heartbeatWatchdog = setTimeout(() => {
      console.warn("[WsClient] heartbeat timeout — closing socket");
      if (this.ws != null) {
        try { this.ws.close(); } catch { /* noop */ }
      }
    }, this.heartbeatInterval * 2);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatWatchdog != null) {
      clearTimeout(this.heartbeatWatchdog);
      this.heartbeatWatchdog = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    const attempt = this.reconnectAttempt++;
    const delay = Math.min(this.reconnectionDelay * Math.pow(2, attempt), this.reconnectionDelayMax);
    this.dispatchReserved("reconnect_attempt", attempt + 1);
    this.reconnectTimer = setTimeout(() => {
      void this.openSocketWithFreshAuth();
    }, delay);
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    for (const [id, pending] of this.pendingAcks) {
      if (pending.timer != null) clearTimeout(pending.timer);
      pending.reject(new Error("disconnected"));
      this.pendingAcks.delete(id);
    }
  }
}

/** Drop-in replacement for `io()` from socket.io-client. */
export function wsConnect<
  ListenEvents extends AnyEventsMap = AnyEventsMap,
  EmitEvents extends AnyEventsMap = AnyEventsMap,
>(opts: WsClientOptions): WsClient<ListenEvents, EmitEvents> {
  return new WsClient<ListenEvents, EmitEvents>(opts);
}
